import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../../lib/utils';
import { 
  Save, Globe, CheckCircle2, AlertCircle, Languages, Info, HelpCircle
} from 'lucide-react';

const languageOptions = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷', nativeName: 'Türkçe' },
  { code: 'en', label: 'İngilizce', flag: '🇺🇸', nativeName: 'English' },
  { code: 'de', label: 'Almanca', flag: '🇩🇪', nativeName: 'Deutsch' },
  { code: 'ru', label: 'Rusça', flag: '🇷🇺', nativeName: 'Русский' },
  { code: 'fr', label: 'Fransızca', flag: '🇫🇷', nativeName: 'Français' },
  { code: 'ar', label: 'Arapça', flag: '🇸🇦', nativeName: 'العربية' },
  { code: 'es', label: 'İspanyolca', flag: '🇪🇸', nativeName: 'Español' }
];

const AdminLanguages = () => {
  const [activeLanguages, setActiveLanguages] = useState(['tr', 'en']);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLanguageSettings();
  }, []);

  const fetchLanguageSettings = async () => {
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      if (res.data.language_settings && Array.isArray(res.data.language_settings.active_languages)) {
        setActiveLanguages(res.data.language_settings.active_languages);
      }
    } catch (err) {
      console.error("Language settings load error", err);
      setError("Dil ayarları yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLanguage = (code) => {
    if (activeLanguages.includes(code)) {
      // Prevent disabling Turkish (tr) as primary fallback
      if (code === 'tr') {
        setError("Türkçe (TR) birincil site dili olduğundan deaktif edilemez.");
        setTimeout(() => setError(''), 4000);
        return;
      }
      
      // Ensure at least one language is active
      if (activeLanguages.length <= 1) {
        setError("En az bir dil aktif olmak zorundadır.");
        setTimeout(() => setError(''), 4000);
        return;
      }
      
      setActiveLanguages(activeLanguages.filter(l => l !== code));
    } else {
      setActiveLanguages([...activeLanguages, code]);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/settings`, {
        key_name: 'language_settings',
        value_data: {
          active_languages: activeLanguages
        }
      }, { withCredentials: true });
      
      setSuccess("Dil ayarları başarıyla kaydedildi.");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save language settings error", err);
      setError("Ayarlar kaydedilirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Alerts */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Settings Form Column */}
        <div className="lg:col-span-2 bg-white rounded-3xl border border-slate-200/60 shadow-sm shadow-slate-100 p-8 space-y-6">
          <div className="space-y-1">
            <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
              <Languages size={18} className="text-blue-600" />
              Çoklu Dil Yönetimi
            </h3>
            <p className="text-[11px] text-slate-500">
              Web sitenizde aktif olmasını istediğiniz dilleri seçin. Seçtiğiniz diller, site header alanında bayrak ikonlarıyla listelenecektir.
            </p>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {languageOptions.map((lang) => {
                const isActive = activeLanguages.includes(lang.code);
                return (
                  <div 
                    key={lang.code}
                    onClick={() => handleToggleLanguage(lang.code)}
                    className={`p-4 rounded-2xl border transition-all duration-200 cursor-pointer flex items-center justify-between group ${
                      isActive 
                        ? 'border-blue-500 bg-blue-50/30' 
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl" role="img" aria-label={lang.label}>
                        {lang.flag}
                      </span>
                      <div>
                        <h4 className="text-xs font-bold text-slate-800">{lang.label}</h4>
                        <p className="text-[10px] text-slate-400 font-medium">{lang.nativeName} ({lang.code.toUpperCase()})</p>
                      </div>
                    </div>

                    {/* Custom Toggle Switch */}
                    <div className={`w-10 h-6 rounded-full p-0.5 transition-colors duration-200 ${
                      isActive ? 'bg-blue-600' : 'bg-slate-200'
                    }`}>
                      <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        isActive ? 'transform translate-x-4' : ''
                      }`} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-2 text-right">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
              >
                <Save size={15} />
                {loading ? "Kaydediliyor..." : "Dil Ayarlarını Kaydet"}
              </button>
            </div>
          </form>
        </div>

        {/* Info Column */}
        <div className="space-y-6">
          <div className="bg-slate-50 border border-slate-200/60 p-6 rounded-3xl space-y-4">
            <h4 className="text-xs font-bold text-slate-800 flex items-center gap-2">
              <Info size={16} className="text-blue-500" />
              Otomatik Çeviri Sistemi Nasıl Çalışır?
            </h4>
            <div className="space-y-3 text-[11px] text-slate-600 leading-relaxed font-medium">
              <p>
                • <strong>Google Translate Entegrasyonu</strong>: Sistem, sitenin ön yüzünde otomatik ve anlık sayfa çevirisi yapmak üzere özel bir Google çeviri köprüsü barındırır.
              </p>
              <p>
                • <strong>Bayraklı Dil Seçici</strong>: Navbar alanında listelenen bayraklardan birine tıklandığında, tüm sayfa içeriği (haberler, sayfalar, donatılar) anında ziyaretçinin seçtiği dile çevrilir.
              </p>
              <p>
                • <strong>SEO Uyumlu Akıllı Slug Çevirisi</strong>: Admin panelinde yeni bir sayfa veya haber eklerken, Türkçe girdiğiniz başlıklar arka planda otomatik olarak İngilizceye çevrilir ve arama motorlarının seveceği tertemiz İngilizce URL takıları (slug) oluşturulur.
              </p>
            </div>
          </div>

          <div className="bg-blue-50/50 border border-blue-100 p-6 rounded-3xl space-y-2 text-[11px] text-slate-600 leading-relaxed">
            <span className="font-bold text-blue-700 block flex items-center gap-1">
              <HelpCircle size={14} /> Dil Eklerken İpucu:
            </span>
            <p>Sitenizi global pazara açmak için İngilizce (EN) ve Almanca (DE) dillerinin aktif tutulması tavsiye edilir.</p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminLanguages;
